<template>
  <base-modal :visible="visible" width="440px" @close="$emit('close')">
    <template #title>
      <i class="fas fa-edit"></i>
      <span>{{ title }}</span>
    </template>

    <!-- Партию можно написать руками или выбрать из ранее созданных значений -->
    <c-f-input
      ref="field"
      v-model="query"
      icon="fas fa-pen"
      placeholder="Введите партию или найдите из списка…"
      no-autofill
      @keydown.native.enter.prevent="useTyped"
    />

    <button v-if="canCreate" type="button" class="bp-create" @click="useTyped">
      <i class="fas fa-plus-circle"></i> Использовать «{{ typed }}»
    </button>

    <button v-if="showRepairAction" type="button" class="bp-repair" @click="$emit('repair'); $emit('close')">
      <i class="fas fa-wrench"></i> Отправить машину на ремонт
    </button>

    <div class="bp-list">
      <template v-if="filtered.length">
        <div class="bp-title">Ранее созданные</div>
        <button
          v-for="name in filtered"
          :key="name"
          type="button"
          class="bp-item"
          :class="{ 'is-current': name === value, 'is-colored': !!colors[name] }"
          :style="itemStyle(name)"
          @click="pick(name)"
        >{{ name }}</button>
      </template>
      <p v-else-if="options.length === 0" class="bp-empty">Созданных партий пока нет: впишите название выше.</p>
      <p v-else class="bp-empty">Ничего не найдено{{ typed ? ': можно использовать введённое' : '' }}.</p>
    </div>

    <template #footer>
      <app-button v-if="value" variant="outline" icon="fas fa-eraser" @click="pick('')">Очистить партию</app-button>
      <app-button variant="ghost" @click="$emit('close')">Отмена</app-button>
    </template>
  </base-modal>
</template>

<script>
import { batchStyle } from '@/batchColor'

// Ячейка «1 партия» / «2 партия» в разнарядке: название вводится вручную или выбирается из ранее созданных значений
// (справочник заказов и маршрутов здесь не используется).
export default {
  name: 'BatchPickerModal',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: 'Партия' },
    // ранее созданные названия партий
    options: { type: Array, default: () => [] },
    // текущее значение ячейки
    value: { type: String, default: '' },
    showRepairAction: { type: Boolean, default: false },
    // цвета партий из справочника партий: название → #RRGGBB (список закрашивается ими)
    colors: { type: Object, default: () => ({}) }
  },
  data() {
    return { query: '' }
  },
  computed: {
    typed() {
      return this.query.trim()
    },
    filtered() {
      const q = this.typed.toLowerCase()
      return q ? this.options.filter((o) => o.toLowerCase().indexOf(q) !== -1) : this.options
    },
    // Введённого названия в списке нет — его можно создать руками
    canCreate() {
      return !!this.typed && this.options.indexOf(this.typed) === -1
    }
  },
  watch: {
    visible(v) {
      if (!v) return
      this.query = ''
      this.$nextTick(() => {
        const input = this.$refs.field && this.$refs.field.$el && this.$refs.field.$el.querySelector('input')
        if (input) input.focus()
      })
    }
  },
  methods: {
    itemStyle(name) {
      return batchStyle(this.colors[name])
    },
    pick(name) {
      this.$emit('select', name)
      this.$emit('close')
    },
    // Enter: введённое совпало с готовым значением — выбирается оно, иначе создаётся новое; пустой ввод ничего не делает
    useTyped() {
      if (this.typed) this.pick(this.typed)
    }
  }
}
</script>

<style scoped>
.bp-create,
.bp-repair {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-6);
  width: 100%;
  margin-top: var(--space-8);
  padding: var(--space-6) var(--space-8);
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}
.bp-create { border: var(--border-width-default) var(--border-style-base) var(--primary); background: var(--primary-soft); color: var(--primary); }
.bp-repair { border: var(--border-width-default) var(--border-style-base) var(--warning); background: var(--warning-soft); color: var(--warning-text); }
.bp-list { margin-top: var(--space-8); max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: var(--space-0-5); }
.bp-title { padding: var(--space-4) var(--space-8) var(--space-2); font-size: var(--text-xs); font-weight: var(--font-weight-semibold); color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.04em; }
.bp-item {
  text-align: left;
  padding: var(--space-5) var(--space-8);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  font-family: inherit;
  font-size: var(--text-base);
  color: var(--text-primary);
  cursor: pointer;
}
.bp-item:hover { background: var(--bg-selected); }
/* закрашенная партия: цвет из справочника партий (inline-стиль) */
.bp-item.is-colored:hover { filter: brightness(0.95); }
.bp-item.is-colored.is-current { box-shadow: inset 0 0 0 2px var(--primary); }
.bp-item.is-current { background: var(--bg-selected); font-weight: var(--font-weight-semibold); }
.bp-empty { margin: 0; padding: var(--space-5) var(--space-8); font-style: italic; color: var(--text-secondary); font-size: var(--text-base); }
</style>
